import { UserTalentInterest } from '@type/v1-api-types';
import {
  FolderPlusIcon, HeartIcon, CheckCircleIcon, UserPlusIcon, HandThumbUpIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { convertToLocalizedDate } from '@utils/date-time/date-time';

interface ApplicantInterestProps extends UserTalentInterest {
  locale?: Locale;
}

const interestLabelKeyMap: Partial<Record<NonNullable<UserTalentInterest['type']>, string>> = {
  application: 'global.label.applied_at',
  saved: 'mc.applicant.saved',
  suggestion: 'mc.applicant.uploaded',
  recommendation: 'applicant.status.24',
};


/**
 * @description Applicant interest component
 * @param type - string - the type of interest (application, saved, like, suggestion, recommendation)
 * @param value - string - the value of interest
 */
export const ApplicantInterest = (props: ApplicantInterestProps) => {
  // Destructure props
  const {
    type,
    value,
    locale,
  } = props;
  const { t } = useTranslation('common');

  // common icons class
  const iconClass = 'w-4 h-4 shrink-0';
  const labelKey = type ? interestLabelKeyMap[type] : undefined;

  let displayValue = value;
  if (typeof value === 'string' && value) {
    try {
      displayValue = convertToLocalizedDate(value, locale);
    } catch {
      displayValue = value;
    }
  }

  if (!displayValue) return null;

  // Render component
  // **********************************************
  return (
    <span className="flex min-w-0 flex-1 items-start gap-1.5 text-xs leading-4">
      { /* Render icon based on type */
        type && (
          <>
            {type === 'application' && <CheckCircleIcon className={iconClass} />}
            {type === 'saved' && <FolderPlusIcon className={iconClass} />}
            {type === 'like' && <HeartIcon className={iconClass} />}
            {type === 'suggestion' && <UserPlusIcon className={iconClass} />}
            {type === 'recommendation' && <HandThumbUpIcon className={iconClass} />}
          </>
        )
      }

      {/* Render value */}
      <span className="line-clamp-2 min-w-0 break-words">
        {labelKey ? t(labelKey) : ''}
        {labelKey && displayValue ? ': ' : ''}
        {displayValue}
      </span>
    </span>
  );
};
